import classNames from 'classnames'
import React, { PropsWithChildren, useState, MouseEvent, memo, CSSProperties } from 'react'
import { Modal, ModalProps } from 'antd'
import { useThrottleFn } from 'ahooks'
import { Merge } from '@/utils/utilityTypes'
import styles from './index.less'

export interface AsyncModalProps {
    /** 确认按钮点击前的检查方法，返回 `true` 表示可以继续执行 `onOk` */
    checkBeforeOk?: () => Promise<boolean> | boolean
    /** 取消按钮点击前的检查方法，返回 `true` 表示可以继续执行 `onCancel` */
    checkBeforeCancel?: () => Promise<boolean> | boolean
    /** 确认按钮点击回调 */
    onOk?: (e: MouseEvent<HTMLElement>) => Promise<void> | void
    /** 取消按钮点击回调 */
    onCancel?: (e: MouseEvent<HTMLElement>) => Promise<void> | void
    /** 自动适应高度 */
    autoFitHeight?: boolean
}

/** Modal 的操作（确认、取消）为异步方法，对应操作按钮自带异步相关 UI 状态 */
export const AsyncModal = memo<PropsWithChildren<Merge<ModalProps, AsyncModalProps>>>(props => {
    const {
        checkBeforeOk = () => true,
        checkBeforeCancel = () => true,
        onOk,
        onCancel,
        autoFitHeight,
        className,
        style,
        children,
        okButtonProps = {},
        cancelButtonProps = {},
        ...modalProps
    } = props

    const mergedClassName = classNames(
        styles.baseModal,
        {
            [styles.autoFitHeight]: autoFitHeight
        },
        className
    )

    const mergedStyle: CSSProperties = { ...(style || {}) }

    const [okBtnLoading, setOkBtnLoading] = useState(false)
    const [cancelBtnLoading, setCancelBtnLoading] = useState(false)

    const { run: handleOk } = useThrottleFn(
        async (e: MouseEvent<HTMLElement>) => {
            const checkBeforeOkBool = await checkBeforeOk?.()
            if (!checkBeforeOkBool) return

            setOkBtnLoading(true)

            try {
                await onOk?.(e)
            } catch (e) {
                console.error('AsyncActionModal onOk error:', e)
            } finally {
                setOkBtnLoading(false)
            }
        },
        { wait: 1000, leading: true, trailing: true }
    )

    const { run: handleCancel } = useThrottleFn(
        async (e: MouseEvent<HTMLElement>) => {
            const checkBeforeCancelBool = await checkBeforeCancel?.()
            if (!checkBeforeCancelBool) return

            setCancelBtnLoading(true)

            try {
                await onCancel?.(e)
            } catch (e) {
                console.error('AsyncActionModal onCancel error:', e)
            } finally {
                setCancelBtnLoading(false)
            }
        },
        { wait: 1000, leading: true, trailing: true }
    )

    return (
        <Modal
            onOk={handleOk}
            onCancel={handleCancel}
            okButtonProps={{ loading: okBtnLoading, disabled: cancelBtnLoading, ...okButtonProps }}
            cancelButtonProps={{ loading: cancelBtnLoading, disabled: okBtnLoading, ...cancelButtonProps }}
            className={mergedClassName}
            style={mergedStyle}
            {...modalProps}
        >
            {children}
        </Modal>
    )
})
